// React and React Router imports
import { Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';

// MSAL (Microsoft Authentication Library) imports
import {
  useMsal,
  useIsAuthenticated,
  MsalAuthenticationTemplate,
} from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';

// Axios for HTTP requests
import axios from 'axios';

// Configuration imports
import { loginRequest } from 'config/authConfig';

// CSS imports
import './App.css';

// Layout and common components
import RootLayout from 'layout/RootLayout';
import RequireAuth from 'hooks/RequireAuth';
import NotFound from 'components/common/errors/NotFound';

// Page components
import HomePage from 'pages/HomePage';
import CoursePage from 'pages/CoursePage';
import SubQuestPage from 'pages/SubQuestPage';
import ErrorPage from 'pages/ErrorPage';
import CourseListPage from 'pages/admin/CourseListPage';
import CourseAdminPage from 'pages/admin/CourseAdminPage';
import CategoryListPage from 'pages/admin/CategoryListPage';
import CompletionReportPage from 'pages/admin/CompletionReportPage';
import SubQuestReportPage from 'pages/admin/SubQuestReportPage';
import SubQuestProfileReportPage from 'pages/admin/SubQuestProfileReportPage';
import LearningPlanListPage from 'pages/admin/LearningPlanListPage';
import LearningPlanProfilePage from 'pages/admin/LearningPlanProfilePage';
import UserProfilePage from 'pages/UserProfilePage';

// Role constants
import { ADMIN, MANAGER, TRAINER } from 'utils/constants/roles';
import ClassListAdminPage from 'pages/admin/ClassListAdminPage';
import ClassCreatePage from 'pages/admin/ClassCreatePage';
import ClassAdminPage from 'pages/admin/ClassAdminPage';
import ClassListPage from 'pages/ClassListPage';
import ClassPage from 'pages/ClassPage';
import ClassCoursePage from 'pages/ClassCoursePage';
import ClassSubQuestPage from 'pages/ClassSubQuestPage';
import CreateQuizPage from 'pages/admin/CreateQuizPage';
import UpdateQuizPage from 'pages/admin/UpdateQuizPage';

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    // Function to acquire a token silently using MSAL
    const acquireTokenSilent = async () => {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      return response.accessToken;
    };

    // Setup Axios interceptors for adding Authorization headers
    const setupAxios = async () => {
      axios.defaults.baseURL = process.env.REACT_APP_OM_API_URL;
      axios.interceptors.request.use(
        async (config) => {
          // Add Authorization header if not already present
          if (!config.headers.Authorization) {
            const authToken = await acquireTokenSilent();
            config.headers.Authorization = `Bearer ${authToken}`;
          }
          return config;
        },
        async (error) => {
          // Retry the request with a new token if a 401 error occurs
          const prevRequest = error?.config;
          if (error?.response?.status === 401 && !prevRequest?.sent) {
            prevRequest.sent = true;
            const newAccessToken = await acquireTokenSilent();
            prevRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(prevRequest);
          }
          return Promise.reject(error);
        }
      );
    };

    // Initialize Axios interceptors if the user is not authenticated
    if (!isAuthenticated) {
      setupAxios();
    }
  }, [isAuthenticated, accounts, instance]);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
    >
      <Routes>
        <Route path="/" element={<RootLayout />}>
          {/* Public routes */}
          <Route index element={<HomePage />} />
          <Route path="/courses/:id" element={<CoursePage />} />
          <Route
            path="/courses/:id/quests/:questId/sub-quests/:subQuestId"
            element={<SubQuestPage />}
          />
          <Route path="/classes" element={<ClassListPage />} />
          <Route path="/classes/:id" element={<ClassPage />} />
          <Route
            path="/classes/:classId/courses/:courseId"
            element={<ClassCoursePage />}
          />
          <Route
            path="/classes/:classId/courses/:courseId/quests/:questId/sub-quests/:subQuestId"
            element={<ClassSubQuestPage />}
          />
          <Route path="/me/profile" element={<UserProfilePage />} />

          {/* Error page for handling access errors */}
          <Route path="/error" element={<ErrorPage />} />

          {/* Protected routes for ADMIN role */}
          <Route element={<RequireAuth allowedRoles={[ADMIN]} />}>
            <Route path="/admin/categories" element={<CategoryListPage />} />
          </Route>

          {/* Protected routes for TRAINER role */}
          <Route element={<RequireAuth allowedRoles={[ADMIN, TRAINER]} />}>
            <Route path="/admin/courses" element={<CourseListPage />} />
            <Route path="/admin/courses/:id" element={<CourseAdminPage />} />

            <Route
              path="/admin/learning-plans"
              element={<LearningPlanListPage />}
            />
            <Route
              path="/admin/learning-plans/:id"
              element={<LearningPlanProfilePage />}
            />
            <Route
              path="/admin/reports/sub-quests"
              element={<SubQuestReportPage />}
            />
            <Route
              path="/admin/reports/sub-quests/:id"
              element={<SubQuestProfileReportPage />}
            />
            <Route path="/admin/quizzes/create" element={<CreateQuizPage />} />
            <Route
              path="/admin/quizzes/edit/:subQuestId"
              element={<UpdateQuizPage />}
            />
          </Route>

          {/* Admin classes routes with custom redirect logic */}
          <Route path="/admin/classes" element={<ClassListAdminPage />} />
          <Route path="/admin/classes/create" element={<ClassCreatePage />} />
          <Route path="/admin/classes/:id" element={<ClassAdminPage />} />

          {/* Protected routes for TRAINER and MANAGER roles */}
          <Route
            element={<RequireAuth allowedRoles={[ADMIN, TRAINER, MANAGER]} />}
          >
            <Route
              path="/admin/reports/completion"
              element={<CompletionReportPage />}
            />
          </Route>

          {/* Fallback route for 404 errors */}
          <Route path="*" element={<NotFound />} />
          <Route path="/404" element={<NotFound />} />
        </Route>
      </Routes>
    </MsalAuthenticationTemplate>
  );
}

export default App;
